/**
 * LA CONFIRMATION DE PRISE EN CHARGE — le calcul, 23/09/2026.
 *
 * Ce que le document doit dire, séparé de la façon dont il l'affiche. Tout ce
 * qui est ici est PUR et testable ; `EcranConfirmation.tsx` ne fait que poser
 * des balises autour.
 *
 * Maquette validée : `docs/maquettes/confirmation-prise-en-charge.html`.
 *
 *
 * LES MONTANTS PASSENT EN CENTIMES, ET N'EN SORTENT QU'À L'AFFICHAGE
 * ---------------------------------------------------------------------------
 * L'API rend `prix_vente` et le montant d'indexation en TEXTE — ce sont des
 * `numeric` PostgreSQL, et le pilote ne les convertit pas en `number` pour
 * cette raison : `0.1 + 0.2` ne vaut pas `0.3` en binaire. On les entre une
 * fois en entiers de centimes, on additionne en entiers, on ne divise qu'au
 * moment d'écrire.
 */

import { appeler, echec, type Resultat } from './api.js';

export interface EtapeConfirmation {
  readonly rang: number;
  readonly type: string;
  readonly site_nom: string;
  readonly site_adresse: string | null;
  readonly site_code_postal: string | null;
  readonly site_ville: string | null;
  readonly site_pays: string | null;
  readonly jour: string;
  readonly heure_debut: string | null;
  readonly heure_fin: string | null;
  readonly quantite: string | null;
  readonly support: string | null;
  readonly poids: number | null;
  readonly ml_unitaire: string | null;
  readonly note: string | null;
}

export interface ClientConfirmation {
  readonly nom: string;
  readonly code: string | null;
  readonly adresse: string | null;
  readonly code_postal: string | null;
  readonly ville: string | null;
  readonly pays: string | null;
  readonly siret: string | null;
  readonly tva: string | null;
  readonly conditions_paiement: string | null;
}

export interface IndexationConfirmation {
  readonly montant: string | null;
  readonly reference: string | null;
  readonly part: string | null;
  readonly valeur_du_mois: string | null;
  readonly manques: readonly string[];
}

export interface ConfirmationLue {
  readonly ordre: {
    readonly numero: string;
    readonly reference_client: string | null;
    readonly marchandise: string | null;
    readonly prix_vente: string | null;
    readonly gabarit: string | null;
  };
  readonly client: ClientConfirmation;
  readonly etapes: readonly EtapeConfirmation[];
  readonly indexation: IndexationConfirmation;
}

/** Un `numeric` rendu en texte, entré en centimes. `null` reste `null`. */
export function centimes(texte: string | null): number | null {
  if (texte === null) return null;
  const valeur = Number(texte);
  if (!Number.isFinite(valeur)) return null;
  return Math.round(valeur * 100);
}

/*
  `duree`, `Manutention` ET `manutention` SONT PARTIS — 28/10/2026.

  Ils calculaient le temps de manutention accordé selon le POIDS de l'envoi et
  la façon dont le rendez-vous était pris, en reprenant les durées du contrat
  type général (article 11). Le document en annonçait UNE, pour tout l'ordre.

  TNS A POSÉ SON PROPRE BARÈME, qui est AU MÉTRAGE et s'applique au métrage de
  CHAQUE arrêt : 45 min jusqu'à 3 ml, 1 h de plus de 3 à 8, 1 h 30 de 8 à 10,
  2 h au-delà. Une valeur unique devenait fausse dès le deuxième arrêt d'une
  ramasse, et le barème est désormais IMPRIMÉ au verso plutôt que calculé — le
  client lit le métrage sur la ligne de son arrêt et conclut lui-même.

  Il n'y a donc plus rien à calculer ici. Les trois sont supprimés avec leurs
  quatorze tests : du code mort n'a rien à faire dans un lot, et un calcul
  qu'aucun écran n'appelle finirait par être cru juste.
*/

export interface Indexation {
  /** Vrai quand la grille ne porte pas de clause : le prix est ferme. */
  readonly incluse: boolean;
  /** Le montant en centimes, `null` tant qu'il n'est pas calculable. */
  readonly montant: number | null;
  readonly phrase: string;
  readonly detail: string | null;
}

/**
 * CE QUE LA GRILLE DU CLIENT DIT DE L'INDEXATION GASOIL.
 *
 * Stafa ne connaît PAS de drapeau « incluse / en sus » : `grilles_tarifaires`
 * porte `indice_reference` et `part_gazole`, les deux ou aucun (contrainte
 * `grilles_indexation_entiere`). Le comportement est donc binaire, et c'est
 * lui qu'on imprime :
 *   - clause remplie → la surcharge S'AJOUTE, en ligne séparée sur la facture ;
 *   - clause vide    → aucune surcharge n'est jamais calculée, le prix est
 *     ferme et l'indexation est donc comprise dedans.
 *
 * Le montant vient de l'API, qui le calcule en `numeric` : on ne refait pas
 * l'arithmétique ici.
 */
export function indexation(lue: IndexationConfirmation): Indexation {
  const sansClause = lue.reference === null || lue.part === null;
  if (sansClause) {
    return { incluse: true, montant: null, phrase: 'comprise dans le prix', detail: null };
  }
  const montant = centimes(lue.montant);
  const detail =
    `part ${lue.part} % · référence ${lue.reference}` +
    (lue.valeur_du_mois === null
      ? ' · indice du mois non publié'
      : ` · indice ${lue.valeur_du_mois}`);
  if (montant === null) {
    return { incluse: false, montant: null, phrase: 'en sus, au taux du mois', detail };
  }
  return {
    incluse: false,
    montant,
    // Afficher un montant puis ne pas l'additionner serait trompeur : quand il
    // est connu il entre dans le total, sinon le total est « hors indexation ».
    phrase: (montant >= 0 ? '+ ' : '− ') + euros(Math.abs(montant)),
    detail,
  };
}

/** Des centimes, en euros à deux décimales, sans le symbole — comme la facture. */
export function euros(cts: number): string {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cts / 100);
}

export interface Charge {
  readonly quantite: number;
  readonly poids: number;
  readonly metres: number;
  readonly support: string | null;
}

/**
 * LA CHARGE ANNONCÉE EST LA SOMME DES CHARGEMENTS.
 *
 * Sur une ramasse, c'est ce qui monte dans le camion — pas le contenu d'un
 * arrêt. Additionner tous les arrêts compterait la marchandise deux fois,
 * une fois chargée et une fois livrée.
 */
export function charge(etapes: readonly EtapeConfirmation[]): Charge {
  const chargements = etapes.filter((e) => e.type === 'chargement');
  let quantite = 0;
  let poids = 0;
  let metres = 0;
  for (const e of chargements) {
    const q = e.quantite === null ? 0 : Number(e.quantite);
    quantite += Number.isFinite(q) ? q : 0;
    poids += e.poids ?? 0;
    const ml = e.ml_unitaire === null ? 0 : Number(e.ml_unitaire);
    if (Number.isFinite(ml) && Number.isFinite(q)) metres += q * ml;
  }
  return { quantite, poids, metres, support: chargements[0]?.support ?? null };
}

/** « 08:00 – 12:00 », ou ce qu'on sait. Les deux heures sont facultatives. */
export function creneau(e: EtapeConfirmation): string {
  if (e.heure_debut !== null && e.heure_fin !== null) {
    return `${e.heure_debut} – ${e.heure_fin}`;
  }
  if (e.heure_debut !== null) return `à partir de ${e.heure_debut}`;
  if (e.heure_fin !== null) return `avant ${e.heure_fin}`;
  // « à confirmer » et pas « horaire à confirmer » : la colonne fait 14 % de
  // 734 px, la forme longue s'y coupe en deux lignes.
  return 'à confirmer';
}

/** « 2026-09-25 » en « 25/09/26 », la forme des lignes de la facture. */
export function jourCourt(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}`;
}

/** « 2026-09-25 » en « 25/09/2026 », la forme du bandeau. */
export function jourLong(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

/**
 * Le repère d'un arrêt dans sa colonne.
 *
 * Numéroté seulement quand il y en a plusieurs du même côté : « ENL. 1 » sur
 * un enlèvement unique ferait chercher un deuxième qui n'existe pas.
 */
export function repere(type: string, rang: number, combien: number): string {
  const chargement = type === 'chargement';
  // Seul l'abrégé court est numéroté : « ENLÈV. 1 » déborderait la colonne.
  if (combien > 1) return `${chargement ? 'ENL.' : 'LIV.'} ${String(rang)}`;
  return chargement ? 'ENLÈV.' : 'LIVR.';
}

export async function lireConfirmation(
  jeton: string,
  otId: string,
): Promise<Resultat<ConfirmationLue>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/confirmation`,
    jeton,
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as ConfirmationLue };
}

// ---------------------------------------------------------------------------
// L'ENVOI AU CLIENT — lot A-bis, 29/10/2026
// ---------------------------------------------------------------------------

export interface ContactPropose {
  readonly nom: string;
  readonly fonction: string | null;
  readonly courriel: string;
}

export interface DestinatairesProposes {
  readonly client: {
    /**
     * RENDU DEPUIS LE 29/10 : l'écran en a besoin pour rattacher à la bonne
     * fiche une adresse tapée à la main qu'on propose de garder en contact.
     */
    readonly id: string;
    readonly nom: string;
    readonly courriel: string | null;
  };
  readonly contacts: readonly ContactPropose[];
  /**
   * LA COPIE PROPOSÉE, et non plus la copie imposée. Depuis le 29/10 l'écran
   * peut la changer — demande de Mohamed, motivée par les relances dont la
   * copie n'a pas à partir à l'exploitation. Ce n'est qu'un point de départ ;
   * le refus d'une copie VIDE reste côté serveur.
   */
  readonly copie: string;
}

export async function lireDestinataires(
  jeton: string,
  otId: string,
): Promise<Resultat<DestinatairesProposes>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/confirmation/destinataires`,
    jeton,
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as DestinatairesProposes };
}

export interface EnvoiFait {
  /**
   * `false` N'EST PAS UNE ERREUR D'APPEL, et c'est pour cela que ce champ
   * existe au lieu d'un refus HTTP : la route répond 200 même quand le serveur
   * de courriel refuse, parce que la trace est écrite et que le message du
   * serveur est ce qu'on veut lire. Un 500 le masquerait derrière
   * « erreur_interne ».
   */
  readonly parti: boolean;
  readonly message: string;
  readonly destinataires: readonly string[];
  /** Ce qui a VRAIMENT été mis en copie, tel que la route le renvoie. */
  readonly copie: readonly string[];
  readonly sujet: string;
}

/**
 * Envoie la confirmation. LE PDF EST FOURNI PAR L'APPELANT, encodé en base64
 * — l'API ne sait pas aller le chercher (`documents.ts` : la clé de service
 * Supabase est interdite dans ce dépôt), et Stafa ne fabrique pas de PDF.
 *
 * LE TEXTE DU COURRIEL N'EST PAS ENVOYÉ D'ICI, délibérément. L'API le
 * reconstruit depuis la base avec la même fonction que le document imprimé :
 * un courriel dont le contenu vient du navigateur est un courriel dont le prix
 * peut être modifié avant l'envoi.
 */
export async function envoyerConfirmation(
  jeton: string,
  otId: string,
  envoi: {
    readonly destinataires: readonly string[];
    readonly copie: readonly string[];
    readonly pieceNom: string;
    readonly pieceBase64: string;
  },
): Promise<Resultat<EnvoiFait>> {
  const echange = await appeler(
    `/ordres-transport/${encodeURIComponent(otId)}/confirmation/envoyer`,
    jeton,
    {
      methode: 'POST',
      corps: {
        destinataires: [...envoi.destinataires],
        copie: [...envoi.copie],
        piece_nom: envoi.pieceNom,
        piece_base64: envoi.pieceBase64,
      },
    },
  );
  if (echange.etat !== 'succes') return echec(echange);
  return { etat: 'ok', valeur: echange.corps as EnvoiFait };
}
